import {
  getPublicActionSessionId,
  trackPublicCta,
  trackPublicImageEdited,
} from '~publish/helpers/publicTracking'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_DIMENSION,
  DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_PIXELS,
  isIdentityImageEdit,
} from '@buffer-mono/popcorn'

import { getImageEditorOutputMimeType } from '~publish/components/ComposerImageEditor/getImageEditorOutputMimeType'
import { renderImageWithinLimits } from '~publish/components/ComposerImageEditor/renderImageWithinLimits'
import { createEditedImageFile } from '~publish/components/ComposerImageEditor/uploadEditedImage'
import { ComposerImageWorkspace } from '~publish/components/ComposerImageWorkspace/ComposerImageWorkspace'
import type {
  ComposerImageWorkspaceImage,
  ComposerImageWorkspaceProps,
  ComposerImageWorkspaceTask,
} from '~publish/components/ComposerImageWorkspace/ComposerImageWorkspace'
import { acquireImageWorkspaceSource } from '~publish/components/ComposerImageWorkspace/imageWorkspaceSourceCache'
import type { ImageWorkspaceSourceLease } from '~publish/components/ComposerImageWorkspace/imageWorkspaceSourceCache'
import type { ImageWorkspaceResult } from '~publish/components/ComposerImageWorkspace/imageWorkspaceSession'
import type { GuestAccountDestination } from '~publish/helpers/guestDraftHandoff'
import type { GuestMedia } from '~publish/helpers/guestMedia'
import { getGuestMediaMaxFileSizeBytes } from '~publish/helpers/guestMedia'
import { getNewImage } from '~publish/legacy/composer/composer/entities/factories'

import { getGuestMediaAccess, uploadGuestMedia } from './guestMediaClient'
import { PublicFeatureSignupDialog } from './PublicFeatureSignupDialog'
import type { PublicFeatureSignupRequest } from './PublicFeatureSignupDialog'

const GUEST_IMAGE_ASPECT_RATIOS = [
  { label: '1:1', value: 1 },
  { label: '9:16', value: 9 / 16 },
] as const
const GUEST_IMAGE_ALT_TEXT_MAX_LENGTH = 1_000

export type GuestImageWorkspaceTask = Extract<
  ComposerImageWorkspaceTask,
  'edit' | 'alt-text'
>

type GuestImageSourceState =
  | Readonly<{ status: 'loading' }>
  | Readonly<{ status: 'error' }>
  | Readonly<{
      status: 'ready'
      image: ComposerImageWorkspaceImage
      lease: ImageWorkspaceSourceLease
    }>

export function PublicGuestImageWorkspace({
  media,
  initialTask,
  currentMediaIds,
  canTagPeople,
  getReturnFocusTarget,
  onReplace,
  onClose,
  onContinueToAccount,
}: {
  media: GuestMedia
  initialTask: GuestImageWorkspaceTask
  currentMediaIds: readonly string[]
  canTagPeople: boolean
  getReturnFocusTarget?: () => HTMLElement | null
  onReplace: (
    replacement: GuestMedia,
    trackingSessionId: string | undefined,
  ) => void
  onClose: () => void
  onContinueToAccount: (destination: GuestAccountDestination) => void
}): JSX.Element {
  const [open, setOpen] = useState(true)
  const [signupRequest, setSignupRequest] =
    useState<PublicFeatureSignupRequest>()
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [sourceState, setSourceState] = useState<GuestImageSourceState>({
    status: 'loading',
  })
  const [workspaceStatus, setWorkspaceStatus] = useState<
    ComposerImageWorkspaceProps['status']
  >({ state: 'idle' })
  const sourceLeaseRef = useRef<ImageWorkspaceSourceLease>()
  const applyControllerRef = useRef<AbortController>()
  const closeFrameRef = useRef<number>()
  const closingRef = useRef(false)
  const placeholderImage = useMemo(
    () =>
      getNewImage({
        url: media.assetUrl,
        width: media.width ?? null,
        height: media.height ?? null,
        altText: media.altText ?? null,
      }),
    [media],
  )

  const releaseSource = useCallback((): void => {
    sourceLeaseRef.current?.release()
    sourceLeaseRef.current = undefined
  }, [])

  const finishClose = useCallback((): void => {
    if (closingRef.current) return

    closingRef.current = true
    applyControllerRef.current?.abort()
    releaseSource()
    setOpen(false)
    closeFrameRef.current = window.requestAnimationFrame(onClose)
  }, [onClose, releaseSource])

  useEffect(() => {
    const controller = new AbortController()
    let acquiredLease: ImageWorkspaceSourceLease | undefined
    setSourceState({ status: 'loading' })
    setWorkspaceStatus({ state: 'idle' })

    const loadSource = async (): Promise<void> => {
      try {
        const access = await getGuestMediaAccess(media, {
          signal: controller.signal,
        })
        if (controller.signal.aborted) return

        acquiredLease = await acquireImageWorkspaceSource(
          access.accessUrl,
          controller.signal,
          undefined,
          { resizeToFit: true },
        )
        if (controller.signal.aborted) {
          acquiredLease.release()
          return
        }

        releaseSource()
        sourceLeaseRef.current = acquiredLease
        const image = getNewImage({
          url: acquiredLease.objectUrl,
          width: acquiredLease.renderImage.naturalWidth,
          height: acquiredLease.renderImage.naturalHeight,
          altText: media.altText ?? null,
        })
        setSourceState({
          status: 'ready',
          lease: acquiredLease,
          image: {
            image,
            sourceStatus: 'ready',
            editorSource: {
              src: acquiredLease.objectUrl,
              alt: media.altText ?? media.fileName,
              width: acquiredLease.renderImage.naturalWidth,
              height: acquiredLease.renderImage.naturalHeight,
            },
          },
        })
      } catch (error) {
        if (
          !controller.signal.aborted &&
          !(error instanceof Error && error.name === 'AbortError')
        ) {
          setSourceState({ status: 'error' })
        }
      }
    }

    if (open) void loadSource()

    return (): void => {
      controller.abort()
      if (sourceLeaseRef.current === acquiredLease) {
        releaseSource()
      } else {
        acquiredLease?.release()
      }
    }
  }, [loadAttempt, media, open, releaseSource])

  useEffect(
    () => (): void => {
      applyControllerRef.current?.abort()
      releaseSource()
      if (closeFrameRef.current !== undefined) {
        window.cancelAnimationFrame(closeFrameRef.current)
      }
    },
    [releaseSource],
  )

  const applyChanges = async (result: ImageWorkspaceResult): Promise<void> => {
    if (sourceState.status !== 'ready') return

    const trackingSessionId = getPublicActionSessionId()
    const resultItem = result.items[0]
    if (!resultItem) {
      finishClose()
      return
    }

    if (isIdentityImageEdit(resultItem.edit)) {
      if (resultItem.altText !== (media.altText ?? '')) {
        onReplace(
          Object.freeze({
            ...media,
            altText: resultItem.altText,
          }),
          trackingSessionId,
        )
      }
      finishClose()
      return
    }

    applyControllerRef.current?.abort()
    const controller = new AbortController()
    applyControllerRef.current = controller
    setWorkspaceStatus({ state: 'applying' })
    let errorType: Extract<
      ComposerImageWorkspaceProps['status'],
      { state: 'error' }
    >['errorType'] = 'imageRenderFailed'

    try {
      const mimeType = getImageEditorOutputMimeType(
        sourceState.lease.blob,
        media.fileName,
        true,
      )
      const renderedImage = await renderImageWithinLimits({
        edit: resultItem.edit,
        limits: {
          maxBytes: getGuestMediaMaxFileSizeBytes(mimeType),
          maxDimension: DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_DIMENSION,
          maxPixels: DEFAULT_IMAGE_EDITOR_MAX_OUTPUT_PIXELS,
          mimeType,
        },
        renderImage: sourceState.lease.renderImage,
      })
      if (controller.signal.aborted) return

      errorType = 'replacementUploadFailed'
      const file = createEditedImageFile(renderedImage)
      const uploadedMedia = await uploadGuestMedia({
        file,
        currentMediaIds,
        mediaId: media.id,
        signal: controller.signal,
      })
      if (controller.signal.aborted) return

      onReplace(
        Object.freeze({
          ...uploadedMedia,
          width: renderedImage.width,
          height: renderedImage.height,
          altText: resultItem.altText,
        }),
        trackingSessionId,
      )
      trackPublicImageEdited(trackingSessionId)
      finishClose()
    } catch (error) {
      if (!controller.signal.aborted) {
        setWorkspaceStatus({
          state: 'error',
          errorType,
          message:
            error instanceof Error
              ? error.message
              : 'The image changes could not be applied. Try again.',
        })
      }
    } finally {
      if (applyControllerRef.current === controller) {
        applyControllerRef.current = undefined
      }
    }
  }

  const readyImage =
    sourceState.status === 'ready' ? sourceState.image : undefined
  const initialImage = readyImage?.image ?? placeholderImage

  return (
    <>
      <ComposerImageWorkspace
        open={open}
        images={readyImage ? [readyImage] : []}
        initialMedia={initialImage}
        initialTask={initialTask}
        aspectRatios={GUEST_IMAGE_ASPECT_RATIOS}
        showFreeAspectRatio
        altText={{
          maxLength: GUEST_IMAGE_ALT_TEXT_MAX_LENGTH,
          onRequestSignup: (trigger): void => {
            trackPublicCta('publish-composer-imageWorkspace-generateAltText-1')
            setSignupRequest({ feature: 'alt-text', trigger })
          },
        }}
        onRequestTaggingSignup={
          canTagPeople
            ? (trigger): void => {
                setSignupRequest({ feature: 'tagging', trigger })
              }
            : undefined
        }
        preparation={
          sourceState.status === 'ready'
            ? undefined
            : sourceState.status === 'loading'
              ? { status: 'loading' }
              : {
                  status: 'error',
                  onRetry: (): void => {
                    setSourceState({ status: 'loading' })
                    setLoadAttempt((attempt) => attempt + 1)
                  },
                }
        }
        status={workspaceStatus}
        getReturnFocusTarget={getReturnFocusTarget}
        onApply={(result): void => void applyChanges(result)}
        onCancel={finishClose}
      />
      {signupRequest ? (
        <PublicFeatureSignupDialog
          {...signupRequest}
          onClose={(): void => setSignupRequest(undefined)}
          onContinueToAccount={onContinueToAccount}
        />
      ) : null}
    </>
  )
}
